import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { app_permissions } from '../../../security/app-permissions';
import { PermissionService } from '../../../security/permission.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { HR_NOTIFICATION_CHANNELS, HR_NOTIFICATION_TOPICS, HrNotificationPreference } from '../../../hr/hr.models';
import { MyHrProfileWorkspace } from '../shared/my-hr-workspace.models';
import { MyHrWorkspaceService } from '../shared/my-hr-workspace.service';
import { labelize, statusSeverity } from '../shared/my-hr-workspace.utils';

@Component({
  selector: 'app-my-hr-profile',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    ButtonModule,
    CheckboxModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    PageDataPanelComponent,
    PageFormSectionComponent
  ],
  providers: [MessageService],
  templateUrl: './my-hr-profile.component.html',
  styleUrls: ['./my-hr-profile.component.scss']
})
export class MyHrProfileComponent implements OnInit {
  private readonly workspaceService = inject(MyHrWorkspaceService);
  private readonly permissionService = inject(PermissionService);
  private readonly messageService = inject(MessageService);

  readonly canManageSelf = this.permissionService.hasPermission(app_permissions.hr.self.manage);
  readonly channelOptions = HR_NOTIFICATION_CHANNELS.map(value => ({ label: labelize(value), value }));
  readonly topicOptions = HR_NOTIFICATION_TOPICS.map(value => ({ label: labelize(value), value }));

  workspace?: MyHrProfileWorkspace;
  saving = false;
  notificationForm: HrNotificationPreference = {
    channel: 'EMAIL',
    topicCode: 'HR_GENERAL',
    enabled: true
  };

  ngOnInit(): void {
    this.load();
  }

  statusSeverity(value?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' {
    return statusSeverity(value);
  }

  label(value?: string): string {
    return labelize(value);
  }

  savePreference(): void {
    if (!this.canManageSelf) {
      return;
    }
    this.saving = true;
    this.workspaceService.saveNotificationPreference(this.notificationForm).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Notification preference updated.' });
        this.notificationForm = {
          channel: 'EMAIL',
          topicCode: 'HR_GENERAL',
          enabled: true,
          deliveryAddress: this.workspace?.profile?.profile?.emailAddress
        };
        this.load();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Profile', detail: 'Notification preference could not be saved.' });
      },
      complete: () => {
        this.saving = false;
      }
    });
  }

  private load(): void {
    this.workspaceService.loadProfileWorkspace().subscribe({
      next: workspace => {
        this.workspace = workspace;
        this.notificationForm = {
          channel: 'EMAIL',
          topicCode: 'HR_GENERAL',
          enabled: true,
          deliveryAddress: workspace.profile?.profile?.emailAddress
        };
      },
      error: () => {
        this.workspace = undefined;
      }
    });
  }
}
